import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

/**
 * React Hook Form field key for a single user group column, e.g. `g12`.
 *
 * The `g` prefix is load-bearing, not cosmetic. RHF's internal `set()` inspects the next path
 * segment and builds an ARRAY when it parses as a number, so a path like
 * `permissions.0.groups.12` would turn `dirtyFields` / `errors` / the field registry into
 * sparse arrays sized by the group id. The prefix also keeps object key order stable, since
 * engines reorder integer-like keys — `compareFormValues` relies on that.
 */
export type UserGroupFieldKey = `g${number}`;

export const toUserGroupFieldKey = (groupId: number): UserGroupFieldKey => `g${groupId}`;

/** RHF path to one checkbox: `permissions.<rowIndex>.groups.<groupKey>` */
export type PermissionGroupFieldPath = `permissions.${number}.groups.${string}`;

export const groupFieldPath = (rowIndex: number, groupKey: string): PermissionGroupFieldPath =>
  `permissions.${rowIndex}.groups.${groupKey}` as PermissionGroupFieldPath;

/**
 * Matches exactly the paths `groupFieldPath` builds. Keep the two in sync — this is the only
 * place the RHF path format is described, in either direction.
 *
 * `g\d+` rather than a permissive segment: the `g` prefix is load-bearing (see above), so a path
 * without it is malformed and must not parse.
 */
const GROUP_FIELD_PATH_PATTERN = /^permissions\.(\d+)\.groups\.(g\d+)$/;

export interface ParsedGroupFieldPath {
  rowIndex: number;
  groupKey: UserGroupFieldKey;
}

/**
 * Inverse of {@link groupFieldPath}: reads a checkbox path back into its row index and group key,
 * or `null` when the path is anything else (RHF reports changes for every field, most of which are
 * not permission checkboxes).
 *
 * The pattern carries the validation, so there is nothing to check afterwards — `\d+` cannot
 * produce `NaN` and the group key cannot come back empty.
 */
export const parseGroupFieldPath = (path: string): ParsedGroupFieldPath | null => {
  const match = GROUP_FIELD_PATH_PATTERN.exec(path);
  if (!match) return null;

  const [, rowIndex, groupKey] = match;

  return { rowIndex: Number(rowIndex), groupKey: groupKey as UserGroupFieldKey };
};

/**
 * Permission row in the table.
 * One row per policy, with the granted state for every user group column.
 */
export interface PermissionRow {
  policy: OpsiumPolicy;
  /** `g${groupId}` -> whether the group is granted this policy */
  groups: Record<string, boolean>;
}

/**
 * User group with its assigned policies.
 * Domain model for a single user group's permissions.
 */
export interface UserGroupPermissions {
  id: number;
  name: string;
  policies: OpsiumPolicy[];
  isDefault: boolean;
}

/**
 * Form data for permissions management
 * Array of permission rows that can be edited in the table
 */
export interface PermissionsFormData {
  permissions: PermissionRow[];
}
